import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    ArrowLeft, Upload, Sparkles, Loader2, Download, Pencil,
    Trash2, X, PartyPopper,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { checkImageFile, checkMessage } from "@/lib/imageCheck";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import PhotoAnnotator from "@/components/PhotoAnnotator";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import ToolJobModal from "@/components/ToolJobModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Upload a file to /api/upload and return the resulting public URL.
// Kept outside the component so React's compiler doesn't flag the async
// state-update pattern inside the body — the component just awaits the
// returned URL and updates its own state.
async function uploadImageToBackend(file) {
    if (!file) return null;
    if (!file.type.startsWith("image/")) {
        toast.error(i18n.t("tools.common.images_only"));
        return null;
    }
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data.url;
}

/**
 * Admin → Tools → AI Photo Retouch.
 * Flow:
 *   1. User drops or picks a photo  → uploaded to /api/upload  → returns url
 *   2. User optionally draws red strokes on it (PhotoAnnotator)
 *   3. User picks a quick-action OR types a Dutch comment (or both)
 *   4. POST /api/tools/photo-retouch/submit  → polls /api/tools/jobs/:id
 *   5. Result shown side-by-side with Before/After slider
 */
export default function AdminToolPhotoRetouch() {
    const { t, i18n } = useTranslation();
    const LOGO_L = ({
        nl: { title: "Logo op de foto", none: "Geen", stamp: "🏷️ Mijn logo", restore: "🔒 Watermerk behouden", pos: "Positie onderaan:", left: "Links", center: "Midden", right: "Rechts", stampTip: "Je logo wordt na de AI-bewerking haarscherp op de foto gezet — zie het voorbeeld op de foto hiernaast.", restoreTip: "Het bestaande watermerk in de foto wordt na de bewerking pixel-perfect teruggeplaatst.", upload: "Logo uploaden (PNG)", replace: "Logo vervangen", uploadFirst: "Upload hier éénmalig je foto-logo (PNG, mag wit zijn) — het wordt onthouden voor alle volgende foto's.", uploadBusy: "Uploaden..." },
        fr: { title: "Logo sur la photo", none: "Aucun", stamp: "🏷️ Mon logo", restore: "🔒 Conserver le filigrane", pos: "Position en bas :", left: "Gauche", center: "Centre", right: "Droite", stampTip: "Votre logo est ajouté net après le traitement IA — voir l'aperçu sur la photo.", restoreTip: "Le filigrane existant est replacé au pixel près après le traitement.", upload: "Télécharger le logo (PNG)", replace: "Remplacer le logo", uploadFirst: "Téléchargez une seule fois votre logo photo (PNG, peut être blanc) — il est mémorisé pour toutes les photos suivantes.", uploadBusy: "Envoi..." },
        en: { title: "Logo on the photo", none: "None", stamp: "🏷️ My logo", restore: "🔒 Keep watermark", pos: "Position at bottom:", left: "Left", center: "Center", right: "Right", stampTip: "Your logo is added razor-sharp after the AI edit — see the preview on the photo.", restoreTip: "The existing watermark is restored pixel-perfect after the edit.", upload: "Upload logo (PNG)", replace: "Replace logo", uploadFirst: "Upload your photo logo once (PNG, can be white) — it is remembered for all next photos.", uploadBusy: "Uploading..." },
        es: { title: "Logo en la foto", none: "Ninguno", stamp: "🏷️ Mi logo", restore: "🔒 Mantener marca de agua", pos: "Posición abajo:", left: "Izquierda", center: "Centro", right: "Derecha", stampTip: "Su logo se añade nítido tras la edición IA — vea la vista previa en la foto.", restoreTip: "La marca de agua existente se restaura píxel a píxel tras la edición.", upload: "Subir logo (PNG)", replace: "Reemplazar logo", uploadFirst: "Suba su logo de foto una sola vez (PNG, puede ser blanco) — se recuerda para todas las fotos siguientes.", uploadBusy: "Subiendo..." },
    })[(i18n.language || "nl").slice(0, 2)] || null;
    const LG = LOGO_L || { title: "Logo op de foto", none: "Geen", stamp: "🏷️ Mijn logo", restore: "🔒 Watermerk behouden", pos: "Positie onderaan:", left: "Links", center: "Midden", right: "Rechts", stampTip: "", restoreTip: "", upload: "Logo uploaden (PNG)", replace: "Logo vervangen", uploadFirst: "", uploadBusy: "..." };
    const [searchParams, setSearchParams] = useSearchParams();
    const isWelcome = searchParams.get("welcome") === "1";
    const [welcomeOpen, setWelcomeOpen] = useState(isWelcome);
    const [state, setState] = useState(null); // { credits_balance, retouch_models, quick_actions }
    const [logoMode, setLogoMode] = useState("none"); // none | stamp | restore
    const [logoPosition, setLogoPosition] = useState("right"); // left | center | right
    const [logoScale, setLogoScale] = useState(20); // % van de fotobreedte (8-40)
    const [logoUploading, setLogoUploading] = useState(false);
    const watermarkInputRef = useRef(null);

    const handleWatermarkLogo = async (file) => {
        if (!file) return;
        if (!String(file.type || "").startsWith("image/")) {
            toast.error("Upload een PNG of JPG logo");
            return;
        }
        setLogoUploading(true);
        try {
            const url = await uploadImageToBackend(file);
            if (url) {
                await api.put("/tools/watermark-logo", { url });
                setState((s) => ({ ...s, logo_url: url }));
                toast.success("Logo opgeslagen — wordt onthouden voor alle volgende foto's");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Logo uploaden mislukt");
        }
        setLogoUploading(false);
    };
    const [sourceUrl, setSourceUrl] = useState(null); // /api/uploads/xxx.jpg
    const [uploading, setUploading] = useState(false);
    const [dropActive, setDropActive] = useState(false);

    const [annotating, setAnnotating] = useState(false);
    const [overlayDataUrl, setOverlayDataUrl] = useState(null);
    const [overlayResetSignal, setOverlayResetSignal] = useState(0);
    const imgRef = useRef(null);
    const fileInputRef = useRef(null);

    const [selectedAction, setSelectedAction] = useState(null);
    const [comment, setComment] = useState("");
    const [model, setModel] = useState("nano-banana-pro");
    // Batch: extra geüploade foto's (urls) onder de hoofdfoto + aangevinkte set
    const [extraPhotos, setExtraPhotos] = useState([]);
    const [batchChecked, setBatchChecked] = useState(new Set());
    // Download-selectie in Recente generaties
    const [dlSelectMode, setDlSelectMode] = useState(false);
    const [dlChecked, setDlChecked] = useState(new Set());

    // Number of jobs still running on the backend — drives the "N op de
    // achtergrond" pill in the header. Derived from `history` after each
    // refresh so we don't track it separately.
    const [pendingCount, setPendingCount] = useState(0);
    const [history, setHistory] = useState([]); // recent retouch jobs (any status)
    const [openJob, setOpenJob] = useState(null); // job opened in the modal
    const historyRef = useRef([]);
    historyRef.current = history;
    const pollRef = useRef(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get("/tools/me");
                if (!alive) return;
                setState(data);
                setLogoMode(data.photo_logo_mode === "stamp" && !data.logo_url ? "none" : (data.photo_logo_mode || "none"));
                setLogoPosition(data.photo_logo_position || "right");
                setLogoScale(data.photo_logo_scale || 20);
            } catch (e) {
                console.error(e);
            }
        })();
        refreshHistory();
        // Background polling: every 5s while the page is open we refresh
        // the history so any jobs finishing in the background surface as
        // "klaar" toasts + appear in the "Recente generaties" grid. This
        // replaces the old blocking single-job poller so the user can
        // keep dropping new photos without waiting for the previous one.
        pollRef.current = setInterval(() => { refreshHistory(); }, 5000);
        return () => {
            alive = false;
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, []);

    // Deep-link support: opening ...?job=<id> auto-opens the ToolJobModal
    // for that specific job. Used by the "Download je foto" mail button so
    // recipients land straight on their finished photo.
    useEffect(() => {
        const jobId = searchParams.get("job");
        if (!jobId) return;
        (async () => {
            try {
                const { data } = await api.get(`/tools/jobs/${jobId}`);
                setOpenJob(data);
                // Keep the URL clean once the modal is populated so a page
                // refresh doesn't keep reopening the same job.
                const next = new URLSearchParams(searchParams);
                next.delete("job");
                setSearchParams(next, { replace: true });
            } catch (e) {
                toast.error(t("tools.retouch.job_not_found"));
            }
        })();
    }, []);

    const retryJob = async (j) => {
        const msg = state?.is_admin ? "Deze foto opnieuw laten bewerken?" : "Deze foto opnieuw laten bewerken? (kost 1 credit)";
        if (!window.confirm(msg)) return;
        try {
            await api.post(`/tools/jobs/${j.id}/retry`);
            toast.success("Opnieuw gestart — je ziet het resultaat zo verschijnen");
            refreshHistory();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Opnieuw proberen mislukt");
        }
    };

    const refreshHistory = async () => {
        try {
            const { data } = await api.get("/tools/jobs", { params: { limit: 30 } });
            const photoJobs = (data.jobs || []).filter((j) => j.tool_key === "photo_retouch");
            // Diff against the previous snapshot so we can toast whenever a
            // background job transitions to completed / failed — the mail
            // notification is asynchronous, this gives instant in-page
            // feedback while the user is still around.
            const prev = historyRef.current || [];
            const prevById = Object.fromEntries(prev.map((j) => [j.id, j.status]));
            for (const j of photoJobs) {
                const before = prevById[j.id];
                if (before && before === "processing" && j.status === "completed") {
                    toast.success(t("tools.retouch.done_toast"));
                }
                if (before && before === "processing" && j.status === "failed") {
                    toast.error(t("tools.retouch.failed_toast", { error: j.error || t("tools.retouch.unknown_error") }));
                }
            }
            setHistory(photoJobs);
            setPendingCount(photoJobs.filter((j) => j.status === "processing").length);
            // Refresh credit balance in case a completed/failed job just
            // refunded credits — cheap call, keeps the UI honest.
            api.get("/tools/me").then(({ data }) => setState(data)).catch(() => {});
        } catch (e) {
            console.error(e);
        }
    };

    const handleFile = async (file) => {
        if (!file) return;
        const chk = await checkImageFile(file);
        if (!chk.ok) {
            toast.error(checkMessage(chk, file.name));
            return;
        }
        setUploading(true);
        try {
            const url = await uploadImageToBackend(file);
            if (url) {
                setSourceUrl(url);
                setOverlayDataUrl(null);
                setOverlayResetSignal((x) => x + 1);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || t("tools.common.upload_failed"));
        }
        setUploading(false);
    };

    // Multi-upload: eerste foto wordt de hoofdfoto (als er nog geen is), de
    // rest komt als thumbnails onder de hoofdfoto (batch-bewerking).
    const handleFiles = async (fileList) => {
        const files = Array.from(fileList || []).filter(Boolean);
        if (files.length === 0) return;
        setUploading(true);
        try {
            for (const f of files) {
                // Controle vóór upload: beschadigd of te klein = geen credits verspillen
                // eslint-disable-next-line no-await-in-loop
                const chk = await checkImageFile(f);
                if (!chk.ok) {
                    toast.error(checkMessage(chk, f.name));
                    continue;
                }
                // eslint-disable-next-line no-await-in-loop
                const url = await uploadImageToBackend(f);
                if (!url) continue;
                setSourceUrl((cur) => {
                    if (!cur) {
                        setOverlayDataUrl(null);
                        setOverlayResetSignal((x) => x + 1);
                        return url;
                    }
                    setExtraPhotos((p) => (p.includes(url) ? p : [...p, url]));
                    return cur;
                });
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || t("tools.common.upload_failed"));
        }
        setUploading(false);
    };

    const useSamplePhoto = async () => {
        try {
            const resp = await fetch("/sample-woning.jpg");
            const blob = await resp.blob();
            const file = new File([blob], "voorbeeld-woning.jpg", { type: "image/jpeg" });
            await handleFile(file);
        } catch {
            toast.error(t("tools.common.sample_failed"));
        }
    };

    const onDrop = async (e) => {
        e.preventDefault();
        setDropActive(false);
        if (e.dataTransfer?.files?.length) await handleFiles(e.dataTransfer.files);
    };

    const uploadOverlayIfNeeded = async () => {
        if (!overlayDataUrl) return null;
        try {
            const blob = await (await fetch(overlayDataUrl)).blob();
            const fd = new FormData();
            fd.append("file", blob, "overlay.png");
            const { data } = await api.post("/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data.url;
        } catch {
            return null;
        }
    };

    // Non-blocking submit: fires the job, immediately clears the workspace
    // so the user can drop another photo right away. Background polling in
    // `refreshHistory` handles the "klaar" toast when the AI is done; the
    // user also receives a "Download je foto" mail with a deep-link back.
    const submit = async () => {
        if (!sourceUrl) {
            toast.error(t("tools.retouch.drop_first"));
            return;
        }
        if (!selectedAction && !comment.trim()) {
            toast.error(t("tools.retouch.choose_action"));
            return;
        }
        if (logoMode === "stamp" && !state?.logo_url) {
            toast.error(LG.uploadFirst || "Upload eerst je foto-logo (PNG) bij 'Logo op de foto'");
            return;
        }
        // Batch: hoofdfoto + alle aangevinkte extra foto's, zelfde instructie
        const targets = [sourceUrl, ...extraPhotos.filter((u) => batchChecked.has(u))];
        setSubmitting(true);
        try {
            const overlayUrl = await uploadOverlayIfNeeded();
            for (let i = 0; i < targets.length; i += 1) {
                // Annotatie-overlay geldt enkel voor de hoofdfoto
                // eslint-disable-next-line no-await-in-loop
                await api.post("/tools/photo-retouch/submit", {
                    source_url: targets[i],
                    overlay_url: i === 0 ? overlayUrl : null,
                    comment_nl: comment.trim() || null,
                    action_preset: selectedAction,
                    model,
                    logo_mode: logoMode,
                    logo_position: logoPosition,
                    logo_scale: logoScale,
                });
            }
            setSourceUrl(null);
            setOverlayDataUrl(null);
            setOverlayResetSignal((x) => x + 1);
            setComment("");
            setSelectedAction(null);
            setAnnotating(false);
            setExtraPhotos([]);
            setBatchChecked(new Set());
            toast.success(targets.length > 1
                ? `${targets.length} foto's in behandeling — resultaten verschijnen hieronder`
                : t("tools.retouch.submitted_toast"));
            refreshHistory();
        } catch (e) {
            toast.error(e.response?.data?.detail || t("tools.retouch.generate_failed"));
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setSourceUrl(null);
        setOverlayDataUrl(null);
        setOverlayResetSignal((x) => x + 1);
        setComment("");
        setSelectedAction(null);
        setAnnotating(false);
        setExtraPhotos([]);
        setBatchChecked(new Set());
    };

    // Load a completed job's result back as the new source so the user can
    // iterate further. Called from the ToolJobModal's "Verder bewerken" button.
    const continueEdit = (j) => {
        if (!j?.result_url) return;
        setSourceUrl(j.result_url);
        setOverlayDataUrl(null);
        setOverlayResetSignal((x) => x + 1);
        setComment("");
        setSelectedAction(null);
        setOpenJob(null);
        // Scroll to top so the user sees the workspace
        window.scrollTo({ top: 0, behavior: "smooth" });
        toast.success(t("tools.retouch.continue_loaded"));
    };

    const balance = state?.credits_balance ?? 0;
    const isAdmin = state?.is_admin;

    return (
        <div className="min-h-screen bg-zinc-50 text-zinc-950" data-testid="tool-photo-retouch-page">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
                {/* Welcome banner — shown right after registration so the new
                    user immediately understands what to do with their 10
                    welcome credits. Dismissable; the query param is removed
                    on close so it never reappears. */}
                {welcomeOpen && (
                    <div
                        data-testid="retouch-welcome-banner"
                        className="mb-8 relative overflow-hidden rounded-sm border border-[#E5A93D]/40 bg-gradient-to-br from-[#E5A93D]/[0.10] via-[#E5A93D]/[0.04] to-transparent p-6 lg:p-7"
                    >
                        <div
                            aria-hidden
                            className="absolute -top-16 -right-16 h-48 w-48 rounded-full blur-3xl bg-[#E5A93D]/30"
                        />
                        <button
                            type="button"
                            data-testid="retouch-welcome-close"
                            onClick={() => {
                                setWelcomeOpen(false);
                                const next = new URLSearchParams(searchParams);
                                next.delete("welcome");
                                setSearchParams(next, { replace: true });
                            }}
                            className="absolute top-3 right-3 text-zinc-500 hover:text-zinc-950 p-1.5 rounded-sm hover:bg-zinc-100 transition"
                            aria-label="Sluiten"
                        >
                            <X className="h-4 w-4" />
                        </button>
                        <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5">
                            <div className="h-14 w-14 rounded-sm bg-[#E5A93D] text-black flex items-center justify-center shrink-0">
                                <PartyPopper className="h-7 w-7" />
                            </div>
                            <div className="flex-1">
                                <p className="text-[10px] uppercase tracking-[0.20em] text-[#E5A93D] font-bold mb-1.5">
                                    {t("tools.retouch.welcome_eyebrow")}
                                </p>
                                <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mb-2">
                                    {t("tools.retouch.welcome_title")}
                                </h2>
                                <p className="text-sm text-zinc-600 max-w-2xl leading-relaxed">
                                    <span className="text-zinc-950 font-semibold">1.</span> {t("tools.retouch.welcome_step1")} · {" "}
                                    <span className="text-zinc-950 font-semibold">2.</span> {t("tools.retouch.welcome_step2")} · {" "}
                                    <span className="text-zinc-950 font-semibold">3.</span> {t("tools.retouch.welcome_step3_pre")} <span className="text-[#E5A93D] font-semibold">{t("tools.retouch.generate")}</span> {t("tools.retouch.welcome_step3_post")}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Header */}
                <div className="flex items-center justify-between gap-6 mb-8">
                    <Link
                        to={state?.is_admin ? "/admin/tools" : "/tools"}
                        className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950"
                        data-testid="tool-back-link"
                    >
                        <ArrowLeft className="h-4 w-4" /> Tools
                    </Link>
                    <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-zinc-500">
                        {pendingCount > 0 && (
                            <span
                                data-testid="retouch-pending-pill"
                                className="inline-flex items-center gap-1.5 rounded-full border border-[#E5A93D]/40 bg-[#E5A93D]/10 px-3 py-1 text-[#E5A93D] normal-case tracking-normal"
                            >
                                <Loader2 className="h-3 w-3 animate-spin" />
                                {t("tools.retouch.pending_pill", { count: pendingCount })}
                            </span>
                        )}
                        {isAdmin ? (
                            <span className="text-[#E5A93D]">{t("tools.common.admin_free")}</span>
                        ) : (
                            <span>{t("tools.common.balance_label")} <span className="text-zinc-950 tabular-nums">{balance}</span> {t("tools.common.credits")}</span>
                        )}
                    </div>
                </div>

                <div className="mb-8">
                    <p className="text-xs uppercase tracking-[0.18em] text-[#E5A93D] mb-3">{t("tools.common.ai_tool")}</p>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
                        {t("tools.retouch.title")}
                    </h1>
                    <p className="mt-3 text-sm text-zinc-500 max-w-2xl">
                        {t("tools.retouch.subtitle")}
                    </p>
                </div>

                {/* History */}
                {history.length > 0 && (() => {
                    const completed = history.filter((j) => j.status === "completed" && j.result_url);
                    const dlIds = [...dlChecked].filter((jid) => completed.some((j) => j.id === jid));
                    return (
                    <div className="mb-8">
                        <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
                            <p className="text-[11px] uppercase tracking-wider text-zinc-500">
                                {t("tools.retouch.recent")}
                            </p>
                            <div className="flex items-center gap-2 flex-wrap">
                                {completed.length > 0 && !dlSelectMode && (
                                    <button
                                        type="button"
                                        onClick={() => { setDlSelectMode(true); setDlChecked(new Set()); }}
                                        className="inline-flex items-center gap-1.5 text-[11px] px-2.5 h-7 border border-zinc-200 text-zinc-600 hover:border-[#E5A93D]/50 hover:text-zinc-950 rounded-sm transition"
                                        data-testid="retouch-dl-select-btn"
                                    >
                                        <Download className="h-3 w-3" /> Selecteer & download
                                    </button>
                                )}
                                {dlSelectMode && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => setDlChecked((s) => (s.size === completed.length ? new Set() : new Set(completed.map((j) => j.id))))}
                                            className="text-[11px] text-zinc-500 hover:text-zinc-950 underline underline-offset-2"
                                            data-testid="retouch-dl-select-all"
                                        >
                                            {dlChecked.size === completed.length ? "Deselecteer alles" : "Selecteer alles"}
                                        </button>
                                        <a
                                            href={dlIds.length > 0 ? `${BACKEND}/api/tools/jobs-zip?ids=${dlIds.slice(0, 100).join(",")}&token=${encodeURIComponent(localStorage.getItem("vsf_token") || "")}` : undefined}
                                            onClick={(e) => { if (dlIds.length === 0) e.preventDefault(); }}
                                            aria-disabled={dlIds.length === 0}
                                            className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 h-7 rounded-sm transition font-bold ${dlIds.length > 0 ? "bg-[#E5A93D] hover:bg-[#C8922C] text-black" : "bg-zinc-100 text-zinc-400 cursor-not-allowed"}`}
                                            data-testid="retouch-download-zip-btn"
                                            title="Download de aangevinkte retouches (AI + origineel) in één ZIP"
                                        >
                                            <Download className="h-3 w-3" /> Download ZIP ({dlIds.length})
                                        </a>
                                        <button
                                            type="button"
                                            onClick={() => { setDlSelectMode(false); setDlChecked(new Set()); }}
                                            className="inline-flex items-center text-[11px] px-2.5 h-7 border border-zinc-200 text-zinc-600 hover:text-zinc-950 rounded-sm transition"
                                            data-testid="retouch-dl-cancel"
                                        >
                                            Annuleer
                                        </button>
                                    </>
                                )}
                                <p className="text-[11px] text-zinc-500">
                                    {t("tools.common.retention")}
                                </p>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                            {history.map((j) => {
                                const selectable = dlSelectMode && j.status === "completed" && j.result_url;
                                const isChecked = dlChecked.has(j.id);
                                return (
                                <button
                                    type="button"
                                    key={j.id}
                                    onClick={() => {
                                        if (selectable) {
                                            setDlChecked((s) => { const n = new Set(s); if (n.has(j.id)) n.delete(j.id); else n.add(j.id); return n; });
                                        } else if (!dlSelectMode && j.status === "failed") {
                                            retryJob(j);
                                        } else if (!dlSelectMode && j.result_url) {
                                            setOpenJob(j);
                                        }
                                    }}
                                    disabled={!j.result_url && j.status !== "failed"}
                                    className={`relative aspect-square bg-white border rounded-sm overflow-hidden group transition-colors text-left disabled:cursor-default ${isChecked ? "border-[#E5A93D] ring-2 ring-[#E5A93D]/50" : "border-zinc-200 hover:border-zinc-400"} ${dlSelectMode && !selectable ? "opacity-40" : ""}`}
                                    data-testid={`history-job-${j.id}`}
                                >
                                    {j.result_url ? (
                                        <>
                                            <img
                                                src={`${BACKEND}${j.result_url}`}
                                                alt=""
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                            {selectable && (
                                                <span className={`absolute top-1.5 left-1.5 h-6 w-6 rounded-sm flex items-center justify-center pointer-events-none ${isChecked ? "bg-[#E5A93D] text-black" : "bg-black/50 border border-white/60"}`} data-testid={`history-check-${j.id}`}>
                                                    {isChecked && <span className="text-xs font-bold">✓</span>}
                                                </span>
                                            )}
                                            {!dlSelectMode && (
                                            <a
                                                href={`${BACKEND}${j.result_url}?dl=ai_retouch_${j.id.slice(0, 8)}.jpg`}
                                                download={`ai_retouch_${j.id.slice(0, 8)}.jpg`}
                                                onClick={(e) => e.stopPropagation()}
                                                data-testid={`history-download-${j.id}`}
                                                title="Download"
                                                className="absolute top-1.5 right-1.5 h-7 w-7 flex items-center justify-center rounded-sm bg-black/70 hover:bg-black text-white opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm"
                                            >
                                                <Download className="h-3.5 w-3.5" />
                                            </a>
                                            )}
                                        </>
                                    ) : j.status === "failed" ? (
                                        <div
                                            className="w-full h-full flex flex-col items-center justify-center gap-1 p-2 bg-red-50/60 text-center"
                                            title={j.error || ""}
                                            data-testid={`history-failed-${j.id}`}
                                        >
                                            <span className="text-[11px] font-bold text-red-600">Mislukt</span>
                                            {j.error && (
                                                <span className="text-[10px] leading-snug text-red-500/90 line-clamp-3 break-words">
                                                    {(j.error.match(/'msg':\s*["']([^"']+)["']/) || [])[1] || j.error}
                                                </span>
                                            )}
                                            <span className="text-[10px] font-bold text-zinc-600 underline underline-offset-2">↻ Opnieuw proberen</span>
                                        </div>
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-[11px] text-zinc-500">
                                            {j.status}
                                        </div>
                                    )}
                                </button>
                                );
                            })}
                        </div>
                    </div>
                    );
                })()}

                <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
                    {/* LEFT — Photo workspace */}
                    <div
                        className="relative bg-white border border-zinc-200 rounded-sm overflow-hidden min-h-[480px] flex flex-col items-stretch justify-start"
                        onDragOver={(e) => { e.preventDefault(); setDropActive(true); }}
                        onDragLeave={() => setDropActive(false)}
                        onDrop={onDrop}
                        data-testid="retouch-workspace"
                    >
                        {!sourceUrl && (
                            <>
                                {/* Stacked layout: slider on top (sharp, fully
                                    interactive), upload prompt below — keeps
                                    the demo crisp instead of blurring it
                                    behind an overlay card. */}
                                <div className="flex items-center justify-center p-3 sm:p-4 bg-zinc-50">
                                    <BeforeAfterSlider
                                        beforeUrl="https://customer-assets-7cd3h4nn.emergentagent.net/job_makelaar-booking/artifacts/ztuzvzpa_image-1790454810950.jpg"
                                        afterUrl="https://customer-assets-7cd3h4nn.emergentagent.net/job_makelaar-booking/artifacts/vcc33jsw_image-1790454813576.jpg"
                                        alt="Voorbeeld: kelder voor en na AI opruiming"
                                        className="max-h-[55vh]"
                                        testId="retouch-demo-slider"
                                    />
                                </div>
                                <div className="text-center px-6 py-7 border-t border-zinc-200 bg-white">
                                    <Upload className="h-8 w-8 mx-auto text-zinc-900 mb-3" />
                                    <p className="text-sm font-medium text-zinc-950 mb-1">Sleep een foto hierheen</p>
                                    <p className="text-xs text-zinc-500 mb-4">JPG of PNG · max 20MB</p>
                                    <Button
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                        data-testid="retouch-pick-file-btn"
                                        className="bg-white text-zinc-950 hover:bg-zinc-100 rounded-sm h-10 font-medium px-6"
                                    >
                                        {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                        {t("tools.common.pick_file")}
                                    </Button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                        onChange={(e) => handleFiles(e.target.files)}
                                        data-testid="retouch-file-input"
                                    />
                                    <div className="mt-3">
                                        <button
                                            type="button"
                                            onClick={useSamplePhoto}
                                            disabled={uploading}
                                            data-testid="retouch-sample-photo-btn"
                                            className="inline-flex items-center gap-2 text-xs font-semibold text-[#E5A93D] hover:text-[#C8922C] underline underline-offset-4 transition-colors disabled:opacity-50"
                                        >
                                            <Sparkles className="h-3.5 w-3.5" />
                                            {t("tools.common.sample_photo_btn")}
                                        </button>
                                    </div>
                                    <p className="mt-3 text-[10px] uppercase tracking-[0.18em] text-[#E5A93D]/90">
                                        {t("tools.common.demo_hint")}
                                    </p>
                                </div>
                            </>
                        )}

                        {sourceUrl && (
                            <div className="relative w-full h-full p-3">
                                <div className="relative inline-block max-w-full">
                                    <img
                                        ref={imgRef}
                                        src={`${BACKEND}${sourceUrl}`}
                                        alt="bron"
                                        className="block max-h-[640px] max-w-full object-contain"
                                        data-testid="retouch-source-image"
                                    />
                                    <PhotoAnnotator
                                        imageRef={imgRef}
                                        active={annotating}
                                        onChange={setOverlayDataUrl}
                                        resetSignal={overlayResetSignal}
                                    />
                                    {/* Live logo-voorbeeld: zo komt je logo op de foto */}
                                    {logoMode === "stamp" && state?.logo_url && (
                                        <img
                                            src={`${BACKEND}${state.logo_url}`}
                                            alt="logo voorbeeld"
                                            className="absolute pointer-events-none drop-shadow"
                                            style={{
                                                width: `${logoScale}%`,
                                                maxHeight: `${Math.round(logoScale * 0.7)}%`,
                                                objectFit: "contain",
                                                bottom: "1.2%",
                                                ...(logoPosition === "left"
                                                    ? { left: "1.2%" }
                                                    : logoPosition === "center"
                                                        ? { left: "50%", transform: "translateX(-50%)" }
                                                        : { right: "1.2%" }),
                                            }}
                                            data-testid="logo-live-preview"
                                        />
                                    )}
                                </div>
                                {/* Floating tools */}
                                <div className="absolute top-3 right-3 flex items-center gap-2">
                                    <Button
                                        size="sm"
                                        onClick={() => setAnnotating((v) => !v)}
                                        data-testid="retouch-toggle-annotate-btn"
                                        className={`rounded-sm h-8 ${annotating ? "bg-[#E5A93D] text-black hover:bg-[#E5A93D]/90" : "bg-zinc-100 text-black hover:bg-zinc-200"}`}
                                    >
                                        <Pencil className="h-3.5 w-3.5 mr-1.5" />
                                        {annotating ? t("tools.retouch.draw_done") : t("tools.retouch.draw")}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={reset}
                                        data-testid="retouch-reset-btn"
                                        className="rounded-sm h-8 text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100"
                                    >
                                        <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {t("tools.common.remove")}
                                    </Button>
                                </div>
                                {/* Batch: extra foto's onder de hoofdfoto — aanvinken = mee bewerken */}
                                <div className="mt-3 pt-3 border-t border-zinc-100">
                                    <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                                        <p className="text-[11px] uppercase tracking-wider text-zinc-500">
                                            Meerdere foto's tegelijk bewerken
                                            {extraPhotos.length > 0 && <span className="text-zinc-400 normal-case tracking-normal"> — vink aan welke mee bewerkt worden</span>}
                                        </p>
                                        <div className="flex items-center gap-2">
                                            {extraPhotos.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => setBatchChecked((s) => (s.size === extraPhotos.length ? new Set() : new Set(extraPhotos)))}
                                                    className="text-[11px] text-zinc-500 hover:text-zinc-950 underline underline-offset-2"
                                                    data-testid="retouch-batch-select-all"
                                                >
                                                    {batchChecked.size === extraPhotos.length ? "Deselecteer alles" : "Selecteer alles"}
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={uploading}
                                                className="inline-flex items-center gap-1.5 text-[11px] px-2.5 h-7 border border-zinc-200 text-zinc-600 hover:border-[#E5A93D]/50 hover:text-zinc-950 rounded-sm transition disabled:opacity-50"
                                                data-testid="retouch-add-more-btn"
                                            >
                                                {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Foto's toevoegen
                                            </button>
                                        </div>
                                    </div>
                                    {extraPhotos.length > 0 && (
                                        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="retouch-batch-strip">
                                            {extraPhotos.map((u, i) => {
                                                const checked = batchChecked.has(u);
                                                return (
                                                    <div key={u} className={`relative aspect-square rounded-sm overflow-hidden border transition ${checked ? "border-[#E5A93D] ring-2 ring-[#E5A93D]/40" : "border-zinc-200"}`}>
                                                        <img
                                                            src={`${BACKEND}${u}`}
                                                            alt={`extra ${i + 1}`}
                                                            loading="lazy"
                                                            className="w-full h-full object-cover cursor-pointer"
                                                            onClick={() => setBatchChecked((s) => { const n = new Set(s); if (n.has(u)) n.delete(u); else n.add(u); return n; })}
                                                            data-testid={`retouch-batch-photo-${i}`}
                                                        />
                                                        <span className={`absolute top-1 left-1 h-5 w-5 rounded-sm flex items-center justify-center pointer-events-none ${checked ? "bg-[#E5A93D] text-black" : "bg-black/50 border border-white/50"}`}>
                                                            {checked && <span className="text-[11px] font-bold">✓</span>}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => { setExtraPhotos((p) => p.filter((x) => x !== u)); setBatchChecked((s) => { const n = new Set(s); n.delete(u); return n; }); }}
                                                            className="absolute top-1 right-1 h-5 w-5 flex items-center justify-center bg-black/60 hover:bg-red-600 text-white rounded-sm"
                                                            title="Verwijder uit lijst"
                                                            data-testid={`retouch-batch-remove-${i}`}
                                                        >
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {dropActive && (
                            <div className="absolute inset-0 border-2 border-dashed border-[#E5A93D] bg-black/40 backdrop-blur-sm flex items-center justify-center pointer-events-none">
                                <p className="text-white text-base">{t("tools.common.drop_to_upload")}</p>
                            </div>
                        )}
                    </div>

                    {/* RIGHT — Controls */}
                    <div className="space-y-5">
                        <div>
                            <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-3">{t("tools.retouch.quick_actions")}</p>
                            <div className="grid grid-cols-2 gap-2">
                                {(state?.quick_actions || []).map((a) => (
                                    <button
                                        key={a.key}
                                        type="button"
                                        onClick={() => setSelectedAction((cur) => cur === a.key ? null : a.key)}
                                        data-testid={`quick-action-${a.key}`}
                                        className={`text-sm h-10 px-3 rounded-sm border transition-all text-left ${
                                            selectedAction === a.key
                                                ? "bg-[#E5A93D] text-black border-[#E5A93D] font-medium"
                                                : "bg-white text-zinc-950 border-zinc-200 hover:border-zinc-400"
                                        }`}
                                    >
                                        {t(`tools.retouch.actions.${a.key}`, a.label)}
                                    </button>
                                ))}
                            </div>
                            {selectedAction && (
                                <button
                                    type="button"
                                    onClick={() => setSelectedAction(null)}
                                    className="mt-2 text-[11px] text-zinc-500 hover:text-zinc-950"
                                >
                                    {t("tools.retouch.clear_selection")}
                                </button>
                            )}
                        </div>

                        <div>
                            <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">
                                {t("tools.retouch.extra_comment")} <span className="text-zinc-400">{t("tools.retouch.optional")}</span>
                            </p>
                            <Textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder={t("tools.retouch.comment_placeholder")}
                                rows={3}
                                data-testid="retouch-comment-input"
                                className="bg-white border-zinc-200 text-sm resize-none"
                            />
                        </div>

                        {/* Logo op de foto — keuze wordt onthouden */}
                        <div className="bg-white border border-zinc-200 rounded-sm p-3" data-testid="logo-options">
                            <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">{LG.title}</p>
                            <div className="flex flex-wrap gap-1.5">
                                {[
                                    { key: "none", label: LG.none },
                                    { key: "stamp", label: LG.stamp },
                                    { key: "restore", label: LG.restore },
                                ].map((o) => (
                                    <button
                                        key={o.key}
                                        type="button"
                                        onClick={() => setLogoMode(o.key)}
                                        className={`h-8 px-3 text-xs font-bold rounded-sm border transition ${logoMode === o.key ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"} disabled:opacity-40 disabled:cursor-not-allowed`}
                                        data-testid={`logo-mode-${o.key}`}
                                    >
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                            {logoMode === "stamp" && (
                                <div className="mt-3 flex items-center gap-3" data-testid="watermark-logo-row">
                                    {state?.logo_url ? (
                                        <div className="h-12 w-20 bg-zinc-800 rounded-sm flex items-center justify-center p-1.5 shrink-0" title="Je foto-logo (donkere achtergrond zodat witte logo's zichtbaar zijn)">
                                            <img src={`${BACKEND}${state.logo_url}`} alt="foto-logo" className="max-h-full max-w-full object-contain" data-testid="watermark-logo-thumb" />
                                        </div>
                                    ) : (
                                        <p className="text-[11px] text-amber-600 font-medium flex-1" data-testid="watermark-logo-missing">{LG.uploadFirst}</p>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => watermarkInputRef.current?.click()}
                                        disabled={logoUploading}
                                        className="h-8 px-3 text-xs font-bold rounded-sm border border-zinc-300 bg-white text-zinc-700 hover:border-zinc-500 transition disabled:opacity-50 shrink-0"
                                        data-testid="watermark-logo-upload-btn"
                                    >
                                        {logoUploading ? LG.uploadBusy : (state?.logo_url ? LG.replace : LG.upload)}
                                    </button>
                                    <input
                                        ref={watermarkInputRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp"
                                        className="hidden"
                                        onChange={(e) => { handleWatermarkLogo(e.target.files?.[0]); e.target.value = ""; }}
                                        data-testid="watermark-logo-input"
                                    />
                                </div>
                            )}
                            {logoMode === "stamp" && (
                                <div className="mt-2 flex items-center gap-1.5" data-testid="logo-position-row">
                                    <span className="text-[11px] text-zinc-500 mr-1">{LG.pos}</span>
                                    {[
                                        { key: "left", label: LG.left },
                                        { key: "center", label: LG.center },
                                        { key: "right", label: LG.right },
                                    ].map((p) => (
                                        <button
                                            key={p.key}
                                            type="button"
                                            onClick={() => setLogoPosition(p.key)}
                                            className={`h-7 px-2.5 text-[11px] font-bold rounded-sm border transition ${logoPosition === p.key ? "bg-[#E5A93D] text-black border-[#E5A93D]" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}
                                            data-testid={`logo-position-${p.key}`}
                                        >
                                            {p.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {logoMode === "stamp" && state?.logo_url && (
                                <div className="mt-2.5 flex items-center gap-3" data-testid="logo-scale-row">
                                    <span className="text-[11px] text-zinc-500 shrink-0">Grootte:</span>
                                    <input
                                        type="range"
                                        min={8}
                                        max={40}
                                        step={1}
                                        value={logoScale}
                                        onChange={(e) => setLogoScale(Number(e.target.value))}
                                        className="flex-1 h-1.5 accent-[#E5A93D] cursor-pointer"
                                        data-testid="logo-scale-slider"
                                    />
                                    <span className="text-[11px] font-bold text-zinc-700 tabular-nums w-9 text-right" data-testid="logo-scale-value">{logoScale}%</span>
                                </div>
                            )}
                            {logoMode === "stamp" && (
                                <p className="mt-2 text-[11px] text-zinc-400">{LG.stampTip}</p>
                            )}
                            {logoMode === "restore" && (
                                <p className="mt-2 text-[11px] text-zinc-400">{LG.restoreTip}</p>
                            )}
                        </div>

                        {(() => {
                            const nSel = 1 + extraPhotos.filter((u) => batchChecked.has(u)).length;
                            return (
                        <Button
                            onClick={submit}
                            disabled={!sourceUrl || submitting || (!selectedAction && !comment.trim())}
                            data-testid="retouch-submit-btn"
                            className="w-full bg-[#E5A93D] hover:bg-[#E5A93D]/90 text-black font-medium rounded-sm h-11"
                        >
                            {submitting ? (
                                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> {t("tools.common.busy")}</>
                            ) : (
                                <><Sparkles className="h-4 w-4 mr-2" /> {t("tools.retouch.generate")}{nSel > 1 ? ` — ${nSel} foto's` : ""} ({isAdmin ? t("tools.common.free") : (nSel > 1 ? `${nSel} credits` : t("tools.common.credit_1"))})</>
                            )}
                        </Button>
                            );
                        })()}
                    </div>
                </div>

            </div>
            <ToolJobModal
                job={openJob}
                onClose={() => setOpenJob(null)}
                onContinueEdit={continueEdit}
            />
        </div>
    );
}
